import React, { useEffect, useState, useCallback, useMemo } from "react";
import baseUrl from "../../../../../api/baseUrl";
import getDecryptedToken from "../../../../../api/getDecryptedToken";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import RestoreIcon from "@mui/icons-material/Restore";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";

interface Employee {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  employeeId?: string;
  roleId?: {
    roleName: string;
    admin?: boolean;
  };
}

const permit = getDecryptedToken("role");

const DeactiveEmployee: React.FC = () => {
  const [tableData, setTableData] = useState<Employee[]>([]);
  const [filteredData, setFilteredData] = useState<Employee[]>([]);
  const [deactivated, setDeactivated] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    baseUrl
      .get("/employee/findEmployee", {
        headers: {
          status: "false",
        },
      })
      .then((response) => {
        setTableData(response.data.data);
        setFilteredData(response.data.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [deactivated]);

  useEffect(() => {
    const filtered = tableData.filter(
      (item) =>
        item?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.roleId?.roleName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchTerm, tableData]);

  const activateEmployee = useCallback((employeeId: string) => {
    if (window.confirm("Are you sure you want to activate this employee?")) {
      baseUrl
        .patch(
          "/employee/activateEmployee",
          {},
          {
            headers: {
              employeeid: employeeId,
            },
          }
        )
        .then(() => {
          setDeactivated((prev) => !prev);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, []);

  // Mobile Card View
  const mobileCards = useMemo(
    () =>
      filteredData.map((item) => (
        <div
          key={item._id}
          className="bg-white rounded-lg p-3 shadow-md border border-slate-100"
        >
          {/* Header with Avatar and Name */}
          <div className="flex items-start justify-between mb-2.5">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center text-white font-semibold shadow-md opacity-60 text-xs">
                {item?.firstName?.charAt(0)}
                {item?.lastName?.charAt(0)}
              </div>
              <div>
                <p className="font-semibold text-xs text-slate-800">
                  {item.firstName + " " + item.lastName}
                </p>
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 text-slate-600 border border-slate-200">
                  {item.roleId?.roleName}
                </span>
              </div>
            </div>
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-red-50 text-red-500">
              <PersonOffIcon sx={{ fontSize: 10 }} />
              Inactive
            </span>
          </div>

          {/* Details */}
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center gap-1.5 text-slate-600">
              <EmailIcon sx={{ fontSize: 14 }} className="text-slate-400" />
              <span className="truncate">{item.email}</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-600">
              <BadgeIcon sx={{ fontSize: 14 }} className="text-slate-400" />
              <span>{item?.employeeId || "N/A"}</span>
            </div>
          </div>

          {/* Action Button */}
          {permit === "Super Admin" && (
            <div className="mt-2.5 pt-2.5 border-t border-slate-100">
              <button
                onClick={() => activateEmployee(item._id)}
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight hover:shadow-lg hover:shadow-primary/20 text-white text-xs font-medium rounded-lg shadow-sm transition-all"
              >
                <RestoreIcon sx={{ fontSize: 16 }} />
                Activate Employee
              </button>
            </div>
          )}
        </div>
      )),
    [filteredData, activateEmployee]
  );

  // Desktop Table Rows
  const tableRows = useMemo(
    () =>
      filteredData.map((item, index) => (
        <tr
          key={item._id}
          className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
        >
          <td className="px-3 py-2 text-xs text-slate-600 font-medium">
            {index + 1}
          </td>
          <td className="px-3 py-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center text-white font-semibold text-xs shadow-md opacity-60">
                {item?.firstName?.charAt(0)}
                {item?.lastName?.charAt(0)}
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-800">
                  {item.firstName + " " + item.lastName}
                </p>
                <p className="text-[10px] text-slate-400">{item?.employeeId}</p>
              </div>
            </div>
          </td>
          <td className="px-3 py-2 text-xs text-slate-600">{item.email}</td>
          <td className="px-3 py-2">
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 text-slate-600 border border-slate-200">
              {item.roleId?.roleName}
            </span>
          </td>
          <td className="px-3 py-2">
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium rounded-md bg-red-50 text-red-500">
              <PersonOffIcon sx={{ fontSize: 12 }} />
              Inactive
            </span>
          </td>
          <td className="px-3 py-2">
            {permit === "Super Admin" && (
              <button
                onClick={() => activateEmployee(item._id)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary to-primaryLight hover:shadow-lg hover:shadow-primary/20 text-white text-xs font-medium rounded-lg shadow-sm transition-all"
              >
                <RestoreIcon sx={{ fontSize: 16 }} />
                Activate
              </button>
            )}
          </td>
        </tr>
      )),
    [filteredData, activateEmployee]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-3 md:p-6">
      <div className=" mx-auto">
        {/* Header */}
        <div className="mb-3 md:mb-4">
          <div className="flex items-center gap-2.5 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-red-100 rounded-lg md:rounded-xl flex items-center justify-center">
              <PersonOffIcon className="text-red-600" sx={{ fontSize: { xs: 18, md: 20 } }} />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-slate-800">
                Deactivated Employees
              </h1>
              <p className="text-slate-500 text-xs md:text-sm mt-0.5">
                Reactivate inactive members
              </p>
            </div>
          </div>
        </div>

        {/* Stats Card */}
        <div className="mb-3 md:mb-4">
          <div className="bg-white rounded-lg md:rounded-xl p-3 md:p-4 shadow-lg shadow-slate-200/50 border border-slate-100 max-w-xs">
            <div className="flex items-center gap-2.5 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-red-50 rounded-md md:rounded-lg flex items-center justify-center">
                <PersonOffIcon className="text-red-500" sx={{ fontSize: { xs: 16, md: 20 } }} />
              </div>
              <div>
                <p className="text-base md:text-lg font-bold text-slate-800">{tableData.length}</p>
                <p className="text-[10px] md:text-xs text-slate-500">Inactive Employees</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar - Mobile */}
        <div className="mb-2 md:hidden">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
            <input
              type="text"
              placeholder="Search inactive employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all shadow-sm text-xs"
            />
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-2">
          {mobileCards}
          {filteredData.length === 0 && (
            <div className="bg-white rounded-lg py-8 text-center shadow-md">
              <CheckCircleIcon className="text-emerald-300 mx-auto mb-3" sx={{ fontSize: 36 }} />
              <p className="text-slate-500 font-medium text-xs">No deactivated employees</p>
              <p className="text-slate-400 text-[10px] mt-1">
                {searchTerm ? "Try adjusting your search" : "All employees are active"}
              </p>
            </div>
          )}
          <div className="text-center py-1.5">
            <p className="text-xs text-slate-500">
              Showing <span className="font-semibold">{filteredData.length}</span> of{" "}
              <span className="font-semibold">{tableData.length}</span>
            </p>
          </div>
        </div>

        {/* Desktop Table Card */}
        <div className="hidden md:block bg-white rounded-[8px] shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
          {/* Search Bar */}
          <div className="p-3 border-b border-slate-200">
            <div className="relative max-w-md">
              <SearchIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                sx={{ fontSize: 16 }}
              />
              <input
                type="text"
                placeholder="Search inactive employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">{tableRows}</tbody>
            </table>

            {/* Empty State */}
            {filteredData.length === 0 && (
              <div className="py-12 text-center">
                <CheckCircleIcon
                  className="text-emerald-300 mx-auto mb-3"
                  sx={{ fontSize: 48 }}
                />
                <p className="text-slate-500 text-sm font-medium">
                  No deactivated employees
                </p>
                <p className="text-slate-400 text-xs mt-1">
                  {searchTerm
                    ? "Try adjusting your search"
                    : "All employees are currently active"}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              Showing{" "}
              <span className="font-semibold text-slate-700">
                {filteredData.length}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-slate-700">
                {tableData.length}
              </span>{" "}
              inactive employees
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeactiveEmployee;
